import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Chip,
    Tooltip,
    Card,
    CardContent,
    Divider,
    CircularProgress,
    useTheme,
    Stack,
    Avatar,
    IconButton,
    Breadcrumbs,
    Link,
    Alert
} from '@mui/material';
import {
    Person as PersonIcon,
    Business as BusinessIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Description as DescriptionIcon,
    CalendarToday as CalendarIcon,
    ArrowBack as ArrowBackIcon,
    GetApp as DownloadIcon,
    AttachMoney as MoneyIcon,
    Assignment as AssignmentIcon,
    NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import axios from 'axios';

const ContractDetails = () => {
    const { contractId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const [contract, setContract] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [companyHistory, setCompanyHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [subscriptionPackages, setSubscriptionPackages] = useState([]);
    const [packagesLoading, setPackagesLoading] = useState(false);
    const [parentContract, setParentContract] = useState(null);
    const [childContracts, setChildContracts] = useState([]);
    const [contracts, setContracts] = useState([]);
    const [contractHistory, setContractHistory] = useState([]);

    useEffect(() => {
        const fetchContractDetails = async () => {
            if (!contractId) {
                setError('Invalid contract ID');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/customer-contracts/${contractId}`);
                const contractData = response.data.data.contract || response.data.data;

                // parent/child contracts provided by the API (may be full objects or just ids)
                let parent = response.data.data.parentContract || response.data.data.parent_contract || contractData.parent_contract_id || null;
                const children = response.data.data.childContracts || response.data.data.child_contracts || [];

                // If the API returned only a parent id (not a full object), try to fetch the parent contract to get its items
                let parentObj = null;
                try {
                    if (parent && typeof parent === 'object') {
                        parentObj = parent;
                    } else if (parent) {
                        // parent is likely an id
                        const pResp = await axios.get(`${process.env.REACT_APP_API_URL}/customer-contracts/${parent}`);
                        parentObj = pResp.data.data.contract || pResp.data.data || null;
                    }
                } catch (err) {
                    // Non-fatal: parent fetch failed — continue without it
                    console.warn('Failed to fetch parent contract for fallback items:', err);
                    parentObj = null;
                }

                // Determine items source:
                // 1) use the contract's own items
                // 2) if none, fall back to the parent contract's items (if available)
                // 3) as a last resort, if API returned children with items (rare when viewing parent), merge them
                let itemsSource = contractData.items;
                if ((!itemsSource || itemsSource.length === 0) && parentObj && parentObj.items && parentObj.items.length > 0) {
                    itemsSource = parentObj.items;
                }
                // If still empty, try to gather items from children (merge)
                if ((!itemsSource || itemsSource.length === 0) && Array.isArray(children) && children.length > 0) {
                    const merged = [];
                    for (const ch of children) {
                        if (ch.items && ch.items.length > 0) merged.push(...ch.items);
                    }
                    if (merged.length > 0) itemsSource = merged;
                }

                // Enhance items with calculated fields (guard when itemsSource may be undefined)
                const enhancedItems = (itemsSource || []).map(item => ({
                    ...item,
                    totalQty: (item.base_quantity || 0) + (item.free_quantity || 0) + (item.extra_quantity || 0),
                    grossValue: ((item.base_quantity || 0) + (item.free_quantity || 0) + (item.extra_quantity || 0)) * (item.price || 0),
                    netAmount: ((item.base_quantity || 0) + (item.extra_quantity || 0)) * (item.price || 0)
                }));

                // If the contract itself lacks a package name, prefer the parent's package name when available
                const mergedContract = { ...contractData };
                if ((!mergedContract.subscription_package && !mergedContract.subscription_package_id && !mergedContract.package_name) && parentObj) {
                    mergedContract.subscription_package = parentObj.subscription_package || parentObj.package_name || parentObj.subscription_package_id;
                }

                setContract({ ...mergedContract, items: enhancedItems });

                setParentContract(parentObj);
                setChildContracts(children);

                // If contract has a company name, fetch other contracts for the same company
                if (contractData.company_name) {
                    setHistoryLoading(true);
                    const historyResponse = await axios.get(`${process.env.REACT_APP_API_URL}/customer-contracts`);
                    const allContracts = historyResponse.data.data;
                    
                    // Filter contracts for the same company, excluding current contract
                    const companyContracts = allContracts.filter(c => 
                        c.company_name === contractData.company_name && 
                        c.id !== contractData.id
                    ).sort((a, b) => new Date(b.created_date) - new Date(a.created_date));
                    
                    setCompanyHistory(companyContracts);
                    setHistoryLoading(false);
                }
            } catch (err) {
                setError('Failed to fetch contract details');
                console.error('Error fetching contract details:', err);
            } finally {
                setLoading(false);
            }
        };

        const fetchContractHistory = async () => {
            try {
                setHistoryLoading(true);
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/customer-contracts/contracts/${contractId}/history`);
                setContractHistory(response.data.contractHistory);
                // if the API returns a single newContract object, normalize into childContracts array
                if (response.data.newContract) {
                    setChildContracts(Array.isArray(response.data.newContract) ? response.data.newContract : [response.data.newContract]);
                }
            } catch (err) {
                console.error('Error fetching contract history:', err);
                setError('Failed to fetch contract history');
            } finally {
                setHistoryLoading(false);
            }
        };

        fetchContractDetails();
        fetchContractHistory();
    }, [contractId]);

    // Fetch subscription packages so we can resolve package IDs to names
    useEffect(() => {
        const fetchPackages = async () => {
            try {
                setPackagesLoading(true);
                const resp = await axios.get(`${process.env.REACT_APP_API_URL}/subscription-packages`);
                // API may return data in different shapes: try common ones
                const data = resp.data?.data ?? resp.data ?? [];
                setSubscriptionPackages(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('Failed to fetch subscription packages', err);
            } finally {
                setPackagesLoading(false);
            }
        };

        fetchPackages();
    }, []);

    const handleDownloadPDF = async () => {
        if (!contractId) {
            setError('Invalid contract ID');
            return;
        }
        try {
            // Fetch the PDF as blob and trigger a download so browsers save the file instead of opening inline
            const resp = await axios.get(`${process.env.REACT_APP_API_URL}/customer-contracts/${contractId}/pdf`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([resp.data], { type: resp.headers['content-type'] }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `contract_${contractId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading PDF:', error);
            setError('Failed to download PDF');
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'active':
                return 'success';
            case 'pending':
                return 'warning';
            case 'expired':
                return 'error';
            case 'draft':
                return 'info';
            default:
                return 'default';
        }
    };

    // Resolve a package name for current contract
    const resolvedPackageName = React.useMemo(() => {
        if (!contract) return null;
        // If API already provided a package name field, prefer it
        if (contract.subscription_package) return contract.subscription_package;

        const pkgId = contract.subscription_package_id || contract.subscription_package;
        if (!pkgId || subscriptionPackages.length === 0) return null;

        const found = subscriptionPackages.find(p => p.id === pkgId || p._id === pkgId || p.package_name === pkgId || p.name === pkgId);
        return found ? (found.package_name || found.name || found.title) : null;
    }, [contract, subscriptionPackages]);

    // Resolve a package name for any contract row (history list)
    const resolvePackageNameFor = (c) => {
        if (!c) return '—';
        // If row already contains a human readable package name, use it
        if (c.package_name && typeof c.package_name === 'string') return c.package_name;
        if (c.subscription_package && typeof c.subscription_package === 'string') return c.subscription_package;

        const pkgId = c.subscription_package || c.subscription_package_id || c.package_name;
        if (!pkgId) return '—';

        // Try to find a matching package in the loaded subscriptionPackages
        const found = subscriptionPackages.find(p => p.id === pkgId || p._id === pkgId || p.package_name === pkgId || p.name === pkgId || String(p.id) === String(pkgId));
        if (found) return found.package_name || found.name || found.title || String(pkgId);

        // Fallback to showing the raw id/value
        return String(pkgId);
    };

    // Build a combined, deduplicated list of contracts to display in History & Renewals
    const combinedContractHistory = useMemo(() => {
        const seen = new Set();
        const out = [];

        const pushIfNew = (entry) => {
            if (!entry) return;
            const id = entry.id ?? entry.contract_id ?? entry._id ?? null;
            if (id == null) return;
            const key = String(id);
            if (!seen.has(key)) {
                seen.add(key);
                out.push(entry);
            }
        };

        // helper: determine if a contract is expired by checking common expiry fields
        const isExpired = (e) => {
            if (!e) return false;
            const exp = e.expiration_date || e.expired_date || e.expirationDate || e.expiry_date || e.end_date || e.expire_date;
            if (!exp) return false;
            try {
                const expDate = new Date(exp);
                const today = new Date();
                expDate.setHours(0,0,0,0);
                today.setHours(0,0,0,0);
                return expDate < today;
            } catch (err) {
                return false;
            }
        };

        // helper: check if any child references the given parent
        const parentHasChild = (parent) => {
            if (!parent || !Array.isArray(childContracts) || childContracts.length === 0) return false;
            const pid = parent.id ?? parent.contract_id ?? parent._id ?? null;
            if (!pid) return false;
            return childContracts.some(ch => {
                const cref = ch.parent_contract_id || ch.parent_contract || ch.parentContract || ch.parent || null;
                const chid = ch.id ?? ch.contract_id ?? ch._id ?? null;
                // if child explicitly points to parent id
                if (cref && String(cref) === String(pid)) return true;
                // or if the child is known and parent id matches parent's id we already have
                return false;
            });
        };

        // Only include parent contract if it doesn't have a child renewal when expired
        if (parentContract) {
            const hideParent = parentHasChild(parentContract) && isExpired(parentContract);
            if (!hideParent) pushIfNew(parentContract);
        }

        if (Array.isArray(childContracts)) childContracts.forEach(pushIfNew);

        if (Array.isArray(companyHistory)) {
            companyHistory.forEach((entry) => {
                // avoid showing an expired parent from companyHistory if it has a child
                const isParentEntry = parentContract && (String(entry.id ?? entry.contract_id ?? entry._id ?? '') === String(parentContract.id ?? parentContract.contract_id ?? parentContract._id ?? ''));
                if (isParentEntry && parentHasChild(parentContract) && isExpired(parentContract)) return;
                pushIfNew(entry);
            });
        }

        return out;
    }, [parentContract, childContracts, companyHistory]);
    

    // Normalize status: if the contract has an expiration date in the past, prefer 'Expired'
    const getNormalizedStatus = (c) => {
        if (!c) return '';
        // try multiple possible expiration date field names
        const exp = c.expiration_date || c.expired_date || c.expirationDate || c.expiry_date || c.end_date || c.expire_date;
        if (exp) {
            try {
                const expDate = new Date(exp);
                const today = new Date();
                expDate.setHours(0,0,0,0);
                today.setHours(0,0,0,0);
                if (expDate < today) return 'Expired';
            } catch (e) {
                // ignore parse errors and fall back to provided status
            }
        }
        // fallback to provided status fields
        return c.status || c.payment_status || '';
    };

    const displayContractDetails = (contractId) => {
        const contract = contracts.find(c => c.id === contractId);

        if (!contract) {
            console.error('Contract not found');
            return;
        }

        const renewalDetails = contractHistory.filter(ch => ch.contract_id === contractId && ch.action === 'renewed');

        return (
            <div>
                <h2>Contract Details</h2>
                <p>Contract ID: {contract.id}</p>
                <p>Customer Name: {contract.customerName}</p>
                <p>Status: {contract.status}</p>
                <p>Expiration Date: {contract.expirationDate}</p>
                {renewalDetails.length > 0 && (
                    <div>
                        <h3>Renewal Details</h3>
                        {renewalDetails.map((rd, index) => (
                            <p key={index}>Renewed to Contract ID: {rd.notes}</p>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                minHeight="60vh"
                gap={2}
            >
                <CircularProgress size={48} />
                <Typography variant="body1" color="text.secondary">
                    Loading contract details...
                </Typography>
            </Box>
        );
    }

    if (error || !contract) {
        return (
            <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error || 'Contract not found'}
                </Alert>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/contracts')}
                    variant="outlined"
                >
                    Back to Contracts
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{
            mx: 'auto',
            p: { xs: 2, md: 3 },
            backgroundColor: theme.palette.grey[50],
            minHeight: '100vh'
        }}>
            {/* Breadcrumbs */}
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                sx={{ mb: 3 }}
                aria-label="breadcrumb"
            >
                <Link
                    underline="hover"
                    color="inherit"
                    onClick={() => navigate('/contracts')}
                    sx={{ cursor: 'pointer' }}
                >
                    Contracts
                </Link>
                <Typography color="text.primary">Contract #{contractId}</Typography>
            </Breadcrumbs>

            {/* Contract History & Renewals Section */}
            <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'grey.200' }}>
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom color="primary.main">
                        Contract History & Renewals
                    </Typography>
                    {/* <Typography variant="body2" color="text.secondary" mb={2}>
                        All previous renewals and contract periods for this company.
                    </Typography> */}
                    {historyLoading ? (
                        <Box display="flex" alignItems="center" gap={2}>
                            <CircularProgress size={24} />
                            <Typography variant="body2">Loading history...</Typography>
                        </Box>
                    ) : companyHistory.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No previous contracts or renewals found for this Contract.
                        </Typography>
                    ) : (companyHistory.length === 0 && !parentContract && childContracts.length === 0) ? (
                        <Typography variant="body2" color="text.secondary">
                            No previous contracts or renewals found for this Contract.
                        </Typography>
                    ) : (
                        <TableContainer component={Paper} sx={{ mt: 2 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Contract ID</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Created Date</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Package Name</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Start Date</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>End Date</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Payment Status</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Renewal</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>View Contract</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {combinedContractHistory.map((c) => (
                                        <TableRow key={c.id}>
                                            <TableCell>#{c.id}</TableCell>
                                            <TableCell>{c.created_date ? new Date(c.created_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}</TableCell>
                                            <TableCell>{resolvePackageNameFor(c)}</TableCell>
                                            <TableCell>{(c.start_date || c.created_date) ? new Date(c.start_date || c.created_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}</TableCell>
                                            <TableCell>{c.expiration_date ? new Date(c.expiration_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}</TableCell>
                                            <TableCell>
                                                {
                                                    (() => {
                                                        const norm = getNormalizedStatus(c);
                                                        return <Chip label={norm || '—'} color={getStatusColor(norm)} size="small" />;
                                                    })()
                                                }
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={c.payment_status || 'N/A'} color={c.payment_status === 'paid' ? 'success' : 'warning'} size="small" />
                                            </TableCell>
                                            <TableCell>
                                                {
                                                    (() => {
                                                        // Determine if this row represents a renewal (child contract)
                                                        const rowId = c.id ?? c.contract_id ?? c._id ?? null;
                                                        const parentRef = c.parent_contract_id || c.parent_contract || c.parentContract || c.parent || null;

                                                        const inChildList = Array.isArray(childContracts) && childContracts.some(ch => {
                                                            const chId = ch.id ?? ch.contract_id ?? ch._id ?? null;
                                                            return chId != null && rowId != null && String(chId) === String(rowId);
                                                        });

                                                        const isRenewal = !!parentRef || inChildList;

                                                        if (isRenewal) {
                                                            const parentIdText = parentRef ? (parentRef.id || parentRef) : (parentContract && parentContract.id ? parentContract.id : '');
                                                            return (
                                                                <Tooltip title={parentIdText ? `Child of #${parentIdText}` : 'Renewal'} arrow>
                                                                    <Chip label="Renewal" color="primary" size="small" />
                                                                </Tooltip>
                                                            );
                                                        }

                                                        return <Chip label="Original" color="default" size="small" />;
                                                    })()
                                                }
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => window.open(`${process.env.REACT_APP_API_URL}/customer-contracts/${c.id}/pdf`, '_blank')}
                                                >
                                                    View
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            {/* Header Section */}
            <Box
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                gap={2}
                mb={4}
            >


            </Box>

            {/* Contract Header Card */}
            <Card
                sx={{
                    mb: 4,
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    border: '1px solid',
                    borderColor: 'grey.200'
                }}
            >                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <IconButton
                                onClick={() => navigate('/contracts')}
                                sx={{
                                    backgroundColor: 'white',
                                    boxShadow: 1,
                                    '&:hover': { backgroundColor: 'grey.100' }
                                }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar
                                    sx={{
                                        backgroundColor: theme.palette.primary.main,
                                        width: 56,
                                        height: 56,
                                        fontSize: '1.5rem'
                                    }}
                                >
                                    <AssignmentIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                                        Contract #{contractId}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {resolvedPackageName || contract.subscription_package || contract.subscription_package_id || 'No package specified'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 'auto' }}>
                            <Button
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadPDF}
                                size="large"
                                sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: 3
                                }}
                            >
                                Contract Download
                            </Button>
                            <Chip
                                label={getNormalizedStatus(contract) || contract.status}
                                color={getStatusColor(getNormalizedStatus(contract) || contract.status)}
                                size="large"
                                sx={{
                                    fontWeight: 600,
                                    px: 2,
                                    height: 36
                                }}
                            />
                        </Box>
                    </Box>
                    <Divider sx={{ my: 3 }} />
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={12}>
                            <Paper
                                elevation={1}
                                sx={{
                                    height: '100%',
                                    p: 3,
                                    borderRadius: 2,
                                    backgroundColor: 'background.paper',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        boxShadow: 3,
                                        transform: 'translateY(-2px)'
                                    }
                                }}
                            >
                                <Typography variant="h6" fontWeight="bold" mb={3} color="text.primary" sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
                                    Customer Information
                                </Typography>
                                <Stack spacing={3}>
                                    <Box display="flex" alignItems="center" gap={3}>
                                        <Avatar sx={{ backgroundColor: 'primary.light', width: 32, height: 32 }}>
                                            <PersonIcon fontSize="small" />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Customer Name
                                            </Typography>
                                            <Typography variant="body1" fontWeight="medium">
                                                {contract.customer_name}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Avatar sx={{ backgroundColor: 'secondary.light', width: 32, height: 32 }}>
                                            <BusinessIcon fontSize="small" />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Company
                                            </Typography>
                                            <Typography variant="body1" fontWeight="medium">
                                                {contract.company_name || 'Not specified'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Avatar sx={{ backgroundColor: 'info.light', width: 32, height: 32 }}>
                                            <EmailIcon fontSize="small" />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Email Address
                                            </Typography>
                                            <Typography variant="body1" fontWeight="medium">
                                                {contract.email}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Avatar sx={{ backgroundColor: 'success.light', width: 32, height: 32 }}>
                                            <PhoneIcon fontSize="small" />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Phone Number
                                            </Typography>
                                            <Typography variant="body1" fontWeight="medium">
                                                {contract.phone || 'Not provided'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Stack>
                            </Paper>                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Paper
                                elevation={1}
                                sx={{
                                    height: '100%',
                                    p: 3,
                                    borderRadius: 2,
                                    backgroundColor: 'background.paper',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        boxShadow: 3,
                                        transform: 'translateY(-2px)'
                                    }
                                }}
                            >
                                <Typography variant="h6" fontWeight="bold" mb={3} color="text.primary" sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
                                    Contract Information
                                </Typography>
                                <Stack spacing={3}>
                                    <Box display="flex" alignItems="center" gap={3}>
                                        <Avatar sx={{ backgroundColor: 'warning.light', width: 32, height: 32 }}>
                                            <DescriptionIcon fontSize="small" />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Package Name
                                            </Typography>
                                            <Typography variant="body1" fontWeight="medium">
                                                {resolvedPackageName || contract.subscription_package || contract.subscription_package_id || 'No package specified'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Avatar sx={{ backgroundColor: 'primary.light', width: 32, height: 32 }}>
                                            <CalendarIcon fontSize="small" />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Created Date
                                            </Typography>
                                            <Typography variant="body1" fontWeight="medium">
                                                {new Date(contract.created_date).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Avatar sx={{ backgroundColor: 'error.light', width: 32, height: 32 }}>
                                            <CalendarIcon fontSize="small" />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Expiration Date
                                            </Typography>
                                            <Typography variant="body1" fontWeight="medium">
                                                {new Date(contract.expiration_date).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {/* Show previous contract expirations when auto-renewal is enabled */}
                                    {(contract?.auto_renewal === 1 || contract?.autoRenewal === true) && companyHistory.length > 0 && (
                                        <Box mt={2}>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                Previous contract expiration dates (auto-renewal history)
                                            </Typography>
                                            <Stack spacing={1}>
                                                {companyHistory.map((hc) => (
                                                    <Typography key={hc.id} variant="body2" color="text.primary">
                                                        {`#${hc.id} — ${new Date(hc.expiration_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`}
                                                    </Typography>
                                                ))}
                                            </Stack>
                                        </Box>
                                    )}
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Avatar sx={{ backgroundColor: 'success.light', width: 32, height: 32 }}>
                                            <MoneyIcon fontSize="small" />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Total Value
                                            </Typography>
                                            <Typography variant="h6" fontWeight="bold" color="primary.main">
                                                ${parseFloat(contract.grand_total || 0).toLocaleString('en-US', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Contract Items */}
            <Card
                sx={{
                    mb: 4,
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    overflow: 'hidden'
                }}
            >
                <Box
                    sx={{
                        p: 3,
                        backgroundColor: theme.palette.primary.main,
                        color: 'white'
                    }}
                >
                    <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" gap={1}>
                        <Box alignItems="baseline" gap={2}>
                            <Typography variant="h6" fontWeight="bold">
                              Selected Package1: {resolvedPackageName || contract.subscription_package || contract.subscription_package_id || 'No package specified'}
                           
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9, mt: { xs: 1, sm: 0 } }}>
                                Detailed breakdown of all items in this contract
                            </Typography>
                        </Box>
                        
                    </Box>
                </Box>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: 'grey.50' }}>
                                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Item Name</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', py: 2 }}>Unit Price</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', py: 2 }}>Base Qty</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', py: 2 }}>Free Qty</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', py: 2 }}>Extra Qty</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', py: 2 }}>Total Qty</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', py: 2 }}>Gross Value</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', py: 2 }}>Net Amount</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {contract.items?.map((item, index) => {
                                const baseQty = parseFloat(item.base_quantity || 0);
                                const freeQty = parseFloat(item.free_quantity || 0);
                                const extraQty = parseFloat(item.extra_quantity || 0);
                                const totalQty = baseQty + freeQty + extraQty;
                                const price = parseFloat(item.price || 0);
                                const totalValue = totalQty * price;
                                const finalTotal = (baseQty + extraQty) * price;

                                return (
                                    <TableRow
                                        key={index}
                                        sx={{
                                            '&:nth-of-type(odd)': { backgroundColor: 'action.hover' },
                                            '&:hover': { backgroundColor: 'action.selected' }
                                        }}
                                    >
                                        <TableCell sx={{ py: 2 }}>
                                            <Typography variant="body1" fontWeight="medium">
                                                {item.item_name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ py: 2 }}>
                                            <Typography variant="body2" fontWeight="medium">
                                                ${price.toLocaleString('en-US', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ py: 2 }}>{baseQty}</TableCell>
                                        <TableCell align="right" sx={{ py: 2 }}>
                                            <Chip
                                                label={freeQty}
                                                size="small"
                                                color="success"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="right" sx={{ py: 2 }}>{extraQty}</TableCell>
                                        <TableCell align="right" sx={{ py: 2 }}>
                                            <Typography variant="body2" fontWeight="bold">
                                                {totalQty}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ py: 2 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                ${totalValue.toLocaleString('en-US', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ py: 2 }}>
                                            <Typography variant="body2" fontWeight="bold" color="primary.main">
                                                ${finalTotal.toLocaleString('en-US', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>

            {/* Billing Summary */}
            <Card
                sx={{
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    border: '1px solid',
                    borderColor: 'grey.200'
                }}
            >
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Billing Summary
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={3}>
                        Final calculation breakdown
                    </Typography>

                    <Divider sx={{ my: 3 }} />

                    <Grid container spacing={3}>
                        <Grid item xs={12} lg={8}>
                            <Stack spacing={2}>
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{ py: 1 }}
                                >
                                    <Typography variant="body1">
                                        Subtotal (Original Amount)
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        ${parseFloat(contract.subtotal || 0).toLocaleString('en-US', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })}
                                    </Typography>
                                </Box>

                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{ py: 1 }}
                                >
                                    <Typography variant="body1" color="success.main">
                                        Contract Discount ({contract.discount_rate || 0}%)
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium" color="success.main">
                                        -${parseFloat(contract?.item_discounts || 0).toLocaleString('en-US', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })}
                                    </Typography>
                                </Box>

                                <Divider />

                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{
                                        py: 2,
                                        backgroundColor: 'primary.light',
                                        borderRadius: 2,
                                        px: 2,
                                        mt: 2
                                    }}
                                >
                                    <Typography variant="h6" fontWeight="bold " color="#FFF">
                                        Total Amount Payable
                                    </Typography>
                                    <Typography variant="h5" fontWeight="bold" color="#FFF" sx={{ ml: 2 }}>
                                        ${parseFloat(contract.grand_total || 0).toLocaleString('en-US', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} lg={4}>
                            <Paper
                                sx={{
                                    p: 3,
                                    backgroundColor: 'grey.50',
                                    borderRadius: 2,
                                    textAlign: 'center'
                                }}
                            >
                                <MoneyIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                                <Typography variant="body2" color="text.secondary" mb={1}>
                                    You Save
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" color="success.main">
                                    ${parseFloat(contract?.item_discounts || 0).toLocaleString('en-US', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    })}
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* New Contract Details & Old Contract History */}
            {/* {childContracts && childContracts.length > 0 && (
                (() => {
                    const nc = childContracts[0];
                    return (
                        <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'grey.200' }}>
                            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                <Typography variant="h6" fontWeight="bold" gutterBottom color="primary.main">
                                    New Contract Details
                                </Typography>
                                <Typography variant="body2" color="text.secondary" mb={2}>
                                    Contract ID: {nc.id}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" mb={2}>
                                    Created Date: {new Date(nc.created_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" mb={2}>
                                    Expired Date: {new Date(nc.expiration_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" mb={2}>
                                    Status: {nc.status}
                                </Typography>
                            </CardContent>
                        </Card>
                    );
                })()
            )} */}

            {/* {contractHistory.length > 0 && (
                <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'grey.200' }}>
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom color="primary.main">
                            Old Contract History
                        </Typography>
                        {contractHistory.map((history) => (
                            <Box key={history.id} sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Action: {history.action}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Start Date: {new Date(history.start_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    End Date: {new Date(history.end_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Notes: {history.notes}
                                </Typography>
                                <Divider sx={{ my: 2 }} />
                            </Box>
                        ))}
                    </CardContent>
                </Card>
            )} */}
        </Box>
    );
};

export default ContractDetails;